import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import BusinessNotificationHelper from '../../services/businessNotificationHelper';

// 类型定义
export interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: string;
  created_at: string;
  profiles?: {
    display_name: string | null;
    avatar_url: string | null;
    bio: string | null;
  } | null;
}

export interface FriendRequest {
  id: string;
  sender_id: string;
  receiver_id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  message: string | null;
  created_at: string;
  sender_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  receiver_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface GroupInvitation {
  id: string;
  group_id: string;
  inviter_id: string;
  invitee_id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  role: string;
  share_ratio: number;
  message: string | null;
  created_at: string;
  group?: {
    name: string;
    description: string | null;
  };
  inviter_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface SearchedUser {
  id: string;
  email: string;
  username: string | null;
  phone: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
}

export interface FriendsState {
  friends: Friend[];
  friendRequests: FriendRequest[];
  groupInvitations: GroupInvitation[];
  searchResults: SearchedUser[];
  loading: boolean;
  searchLoading: boolean;
  error: string | null;
}

const initialState: FriendsState = {
  friends: [],
  friendRequests: [],
  groupInvitations: [],
  searchResults: [],
  loading: false,
  searchLoading: false,
  error: null,
};

// 异步操作：获取好友列表
export const getFriends = createAsyncThunk(
  'friends/getFriends',
  async (userId: string, { rejectWithValue }) => {
    try {
      // 先获取好友关系
      const { data: friendsData, error: friendsError } = await supabase
        .from('friends')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (friendsError) throw friendsError;

      if (!friendsData || friendsData.length === 0) {
        return [];
      }

      // 然后为每个好友获取profile信息
      const friendsWithProfiles = await Promise.all(
        friendsData.map(async (friend) => {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('display_name, avatar_url, bio')
            .eq('id', friend.friend_id)
            .single();

          return {
            ...friend,
            profiles: profileData || null,
          };
        })
      );

      return friendsWithProfiles as Friend[];
    } catch (error: any) {
      // 如果关联查询失败，尝试简单查询
      try {
        const { data, error } = await supabase
          .from('friends')
          .select('*')
          .eq('user_id', userId)
          .eq('status', 'active');

        if (error) throw error;
        return data as Friend[];
      } catch (simpleError: any) {
        return rejectWithValue(simpleError.message);
      }
    }
  }
);

// 异步操作：发送好友请求
export const sendFriendRequest = createAsyncThunk(
  'friends/sendFriendRequest',
  async (
    { 
      friendEmail, 
      friendUsername, 
      friendPhone, 
      message 
    }: { 
      friendEmail?: string; 
      friendUsername?: string; 
      friendPhone?: string; 
      message?: string; 
    },
    { rejectWithValue, getState }
  ) => {
    try {
      // 从Redux state获取当前用户
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      if (!currentUser) {
        throw new Error('请先登录');
      }

      // 确定搜索标识符和类型
      let identifier = '';
      let searchType = 'auto';
      
      if (friendEmail) {
        identifier = friendEmail;
        searchType = 'email';
      } else if (friendPhone) {
        identifier = friendPhone;
        searchType = 'phone';
      } else if (friendUsername) {
        identifier = friendUsername;
        searchType = 'username';
      } else {
        throw new Error('请提供邮箱、用户名或手机号');
      }

      // 使用统一的查找函数
      const { data: userResult, error: userError } = await supabase
        .rpc('find_user_by_email_or_username', { identifier });

      if (userError) {
        console.error('Query error:', userError);
        throw new Error('查找用户时出错: ' + userError.message);
      }

      if (!userResult || userResult.length === 0) {
        let errorMessage = '未找到该';
        if (searchType === 'email') {
          errorMessage += '邮箱对应的用户，请确认邮箱是否正确';
        } else {
          errorMessage += '用户名对应的用户，请确认用户名是否正确';
        }
        throw new Error(errorMessage);
      }

      const targetUserId = userResult[0].id;

      // 不能添加自己为好友
      if (targetUserId === currentUser.id) {
        throw new Error('不能添加自己为好友');
      }

      // 检查是否已经是好友
      const { data: existingFriend } = await supabase
        .from('friends')
        .select('id')
        .eq('user_id', currentUser.id)
        .eq('friend_id', targetUserId)
        .eq('status', 'active')
        .single();

      if (existingFriend) {
        throw new Error('该用户已经是您的好友');
      }

      // 检查是否已经发送过请求（任何状态）
      const { data: existingRequest } = await supabase
        .from('friend_requests')
        .select('id, status')
        .eq('sender_id', currentUser.id)
        .eq('receiver_id', targetUserId)
        .single();

      if (existingRequest) {
        if (existingRequest.status === 'pending') {
          throw new Error('已经向该用户发送过好友请求，请等待对方回应');
        } else {
          // 如果有非pending状态的请求，先删除旧请求
          await supabase
            .from('friend_requests')
            .delete()
            .eq('sender_id', currentUser.id)
            .eq('receiver_id', targetUserId);
          console.log('清理了旧的好友请求记录');
        }
      }

      // 发送好友请求
      const { data, error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: currentUser.id,
          receiver_id: targetUserId,
          message: message || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw new Error('发送好友请求失败: ' + error.message);
      }

      // 发送远程推送给目标用户，确认通知给发送者
      try {
        const notificationHelper = BusinessNotificationHelper.getInstance();
        
        // 获取双方用户信息
        const [senderProfile, targetProfile] = await Promise.all([
          supabase
            .from('profiles')
            .select('display_name')
            .eq('id', currentUser.id)
            .single(),
          supabase
            .from('profiles')
            .select('display_name')
            .eq('id', targetUserId)
            .single()
        ]);
        
        const senderName = senderProfile.data?.display_name || currentUser.email || '用户';
        const targetName = targetProfile.data?.display_name || '该用户';
        
        // 发送远程推送给目标用户
        await notificationHelper.sendFriendInvitation({
          fromUser: senderName,
          fromUserId: currentUser.id,
          targetUserId: targetUserId,
          invitationId: data.id
        });
        
        // 发送确认通知给发送者
        await notificationHelper.sendFriendInvitationConfirmation({
          targetUserName: targetName,
          invitationId: data.id
        });
        
        console.log('好友邀请推送已发送给目标用户，确认通知已显示');
      } catch (notificationError) {
        console.error('发送通知失败，但好友请求已成功发送:', notificationError);
      }

      return data as FriendRequest;
    } catch (error: any) {
      console.error('sendFriendRequest error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：搜索用户
export const searchUsers = createAsyncThunk(
  'friends/searchUsers',
  async (
    { query }: { query: string },
    { rejectWithValue }
  ) => {
    try {
      if (!query.trim()) {
        return [];
      }

      // 使用统一的查找函数，自动识别邮箱和用户名
      const { data: userResult, error: userError } = await supabase
        .rpc('find_user_by_email_or_username', { identifier: query.trim() });

      if (userError) {
        console.error('Search error:', userError);
        throw new Error(`搜索用户时出错: ${userError.message}`);
      }

      console.log('Search result:', userResult); // 调试日志
      
      return userResult || [];
    } catch (error: any) {
      console.error('searchUsers error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：删除好友
export const removeFriend = createAsyncThunk(
  'friends/removeFriend',
  async (
    { friendId }: { friendId: string },
    { rejectWithValue, getState }
  ) => {
    try {
      // 从Redux state获取当前用户
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      if (!currentUser) {
        throw new Error('请先登录');
      }

      // 直接删除好友关系记录（双向删除）
      // 删除当前用户到好友的关系
      const { error: deleteError1 } = await supabase
        .from('friends')
        .delete()
        .eq('user_id', currentUser.id)
        .eq('friend_id', friendId)
        .eq('status', 'active');

      // 删除好友到当前用户的关系  
      const { error: deleteError2 } = await supabase
        .from('friends')
        .delete()
        .eq('user_id', friendId)
        .eq('friend_id', currentUser.id)
        .eq('status', 'active');

      // 清理相关的好友请求记录（双向清理，所有状态）
      const { error: deleteRequestError1 } = await supabase
        .from('friend_requests')
        .delete()
        .eq('sender_id', currentUser.id)
        .eq('receiver_id', friendId);

      const { error: deleteRequestError2 } = await supabase
        .from('friend_requests')
        .delete()
        .eq('sender_id', friendId)
        .eq('receiver_id', currentUser.id);

      if (deleteError1 || deleteError2) {
        console.error('Delete friend error:', deleteError1 || deleteError2);
        throw new Error('删除好友失败: ' + (deleteError1?.message || deleteError2?.message));
      }

      // 好友请求删除失败不影响整体操作，只记录日志
      if (deleteRequestError1 || deleteRequestError2) {
        console.warn('Delete friend requests warning:', deleteRequestError1 || deleteRequestError2);
      } else {
        console.log('✅ 好友请求记录已清理');
      }

      return { friendId };
    } catch (error: any) {
      console.error('removeFriend error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：响应好友请求
export const respondToFriendRequest = createAsyncThunk(
  'friends/respondToFriendRequest',
  async (
    { requestId, action }: { requestId: string; action: 'accept' | 'reject' },
    { rejectWithValue }
  ) => {
    try {
      // 更新好友请求状态
      const { data: request, error: updateError } = await supabase
        .from('friend_requests')
        .update({ status: action === 'accept' ? 'accepted' : 'rejected' })
        .eq('id', requestId)
        .select()
        .single();

      if (updateError) throw updateError;

      // 如果接受，创建双向好友关系
      if (action === 'accept') {
        // 先检查好友关系是否已存在
        const { data: existingFriends } = await supabase
          .from('friends')
          .select('id, user_id, friend_id')
          .or(`and(user_id.eq.${request.receiver_id},friend_id.eq.${request.sender_id}),and(user_id.eq.${request.sender_id},friend_id.eq.${request.receiver_id})`);

        const existingPairs = new Set(
          (existingFriends || []).map(f => `${f.user_id}-${f.friend_id}`)
        );

        const toInsert = [];
        
        // 只插入不存在的记录
        if (!existingPairs.has(`${request.receiver_id}-${request.sender_id}`)) {
          toInsert.push({
            user_id: request.receiver_id,
            friend_id: request.sender_id,
            status: 'active',
          });
        }
        
        if (!existingPairs.has(`${request.sender_id}-${request.receiver_id}`)) {
          toInsert.push({
            user_id: request.sender_id,
            friend_id: request.receiver_id,
            status: 'active',
          });
        }

        if (toInsert.length > 0) {
          const { error: friendError } = await supabase
            .from('friends')
            .insert(toInsert);

          if (friendError) {
            // 如果仍然有冲突错误，忽略它（可能是并发请求导致）
            if (!friendError.message?.includes('duplicate key') && !friendError.code?.includes('23505')) {
              throw friendError;
            }
            console.warn('handleFriendRequest duplicate key ignored:', friendError.message);
          }
        }
      }

      return request as FriendRequest;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：获取好友请求
export const getFriendRequests = createAsyncThunk(
  'friends/getFriendRequests',
  async (userId: string, { rejectWithValue }) => {
    try {
      // 先获取好友请求
      const { data: requestsData, error: requestsError } = await supabase
        .from('friend_requests')
        .select('*')
        .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      // 然后为每个请求获取发送者和接收者的profile信息
      const requestsWithProfiles = await Promise.all(
        requestsData.map(async (request) => {
          // 获取发送者profile
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url, username')
            .eq('id', request.sender_id)
            .single();

          // 获取接收者profile
          const { data: receiverProfile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url, username')
            .eq('id', request.receiver_id)
            .single();

          return {
            ...request,
            sender_profile: senderProfile || null,
            receiver_profile: receiverProfile || null,
          };
        })
      );

      return requestsWithProfiles as FriendRequest[];
    } catch (error: any) {
      // 如果关联查询失败，尝试简单查询
      try {
        const { data, error } = await supabase
          .from('friend_requests')
          .select('*')
          .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data as FriendRequest[];
      } catch (simpleError: any) {
        return rejectWithValue(simpleError.message);
      }
    }
  }
);

// 异步操作：邀请好友加入项目
export const inviteFriendToGroup = createAsyncThunk(
  'friends/inviteFriendToGroup',
  async (
    {
      groupId,
      friendId,
      role = 'member',
      message,
    }: {
      groupId: string;
      friendId: string;
      role?: string;
      message?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // 获取当前用户
      const currentUser = (await supabase.auth.getUser()).data.user;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      // 检查是否已经发送过邀请
      const { data: existingInvitation } = await supabase
        .from('group_invitations')
        .select('id, status')
        .eq('group_id', groupId)
        .eq('invitee_id', friendId)
        .eq('status', 'pending')
        .single();

      if (existingInvitation) {
        throw new Error('已经向该用户发送过邀请，请等待回应');
      }

      // 检查用户是否已经是项目成员
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', groupId)
        .eq('user_id', friendId)
        .single();

      if (existingMember) {
        throw new Error('该用户已经是项目成员');
      }

      // 发送邀请
      const { data, error } = await supabase
        .from('group_invitations')
        .insert({
          group_id: groupId,
          inviter_id: currentUser.id,
          invitee_id: friendId,
          role,
          share_ratio: 1.0, // 默认分摊比例
          message: message || null,
        })
        .select()
        .single();

      if (error) throw error;

      // 注意：项目邀请通知应该在接收方检查邀请时显示
      // 当前的通知系统只能发送给当前用户，不能发送给其他用户
      // 需要在接收方登录或刷新时检查新的项目邀请并显示通知
      console.log('项目邀请已发送，通知将在接收方检查邀请时显示');

      return data as GroupInvitation;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：获取项目邀请
export const getGroupInvitations = createAsyncThunk(
  'friends/getGroupInvitations',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log('获取群组邀请，用户ID:', userId);
      
      // 首先获取邀请基本信息
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('group_invitations')
        .select('*')
        .eq('invitee_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      console.log('群组邀请查询结果:', invitationsData);
      console.log('群组邀请查询错误:', invitationsError);

      if (invitationsError) throw invitationsError;

      if (!invitationsData || invitationsData.length === 0) {
        console.log('没有找到群组邀请');
        return [];
      }

      // 分步查询相关信息以避免关联查询问题
      const enrichedInvitations = await Promise.all(
        invitationsData.map(async (invitation) => {
          try {
            // 获取项目信息
            const { data: groupData } = await supabase
              .from('groups')
              .select('name, description')
              .eq('id', invitation.group_id)
              .single();

            // 获取邀请人信息
            const { data: inviterData } = await supabase
              .from('profiles')
              .select('display_name, avatar_url')
              .eq('id', invitation.inviter_id)
              .single();

            return {
              ...invitation,
              group: groupData,
              inviter_profile: inviterData
            };
          } catch (error) {
            console.warn('获取邀请详情失败:', error);
            return invitation; // 返回基本信息
          }
        })
      );

      console.log('enriched邀请数据:', enrichedInvitations);
      return enrichedInvitations as GroupInvitation[];
    } catch (error: any) {
      console.error('获取群组邀请失败:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：响应项目邀请
export const respondToGroupInvitation = createAsyncThunk(
  'friends/respondToGroupInvitation',
  async (
    { invitationId, action }: { invitationId: string; action: 'accept' | 'reject' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // 更新邀请状态
      const { data: invitation, error: updateError } = await supabase
        .from('group_invitations')
        .update({ status: action === 'accept' ? 'accepted' : 'rejected' })
        .eq('id', invitationId)
        .select()
        .single();

      if (updateError) throw updateError;

      // 如果接受，将用户添加到项目成员中
      if (action === 'accept') {
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({
            group_id: invitation.group_id,
            user_id: invitation.invitee_id,
            role: invitation.role,
            share_ratio: invitation.share_ratio,
            status: 'active',
          });

        if (memberError) throw memberError;

        console.log('邀请接受成功，用户已加入项目:', invitation.group_id);
        
        // 返回包含action信息的结果，以便其他地方可以监听
        return { ...invitation, actionType: action, shouldRefreshGroups: true };
      }

      return { ...invitation, actionType: action, shouldRefreshGroups: false };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const friendsSlice = createSlice({
  name: 'friends',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // getFriends
      .addCase(getFriends.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFriends.fulfilled, (state, action) => {
        state.loading = false;
        state.friends = action.payload;
      })
      .addCase(getFriends.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // sendFriendRequest
      .addCase(sendFriendRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendFriendRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.friendRequests.push(action.payload);
      })
      .addCase(sendFriendRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // searchUsers
      .addCase(searchUsers.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload as string;
        state.searchResults = [];
      })
      // respondToFriendRequest
      .addCase(respondToFriendRequest.fulfilled, (state, action) => {
        const index = state.friendRequests.findIndex(req => req.id === action.payload.id);
        if (index !== -1) {
          state.friendRequests[index] = action.payload;
        }
      })
      // getFriendRequests
      .addCase(getFriendRequests.fulfilled, (state, action) => {
        state.friendRequests = action.payload;
      })
      // removeFriend
      .addCase(removeFriend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFriend.fulfilled, (state, action) => {
        state.loading = false;
        // 从好友列表中移除被删除的好友
        state.friends = state.friends.filter(friend => friend.friend_id !== action.payload.friendId);
      })
      .addCase(removeFriend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getGroupInvitations
      .addCase(getGroupInvitations.fulfilled, (state, action) => {
        state.groupInvitations = action.payload;
      })
      // respondToGroupInvitation
      .addCase(respondToGroupInvitation.fulfilled, (state, action) => {
        const invitation = action.payload;
        
        if (invitation.status === 'accepted' || invitation.status === 'rejected') {
          // 如果邀请已被处理（接受或拒绝），从pending列表中移除
          state.groupInvitations = state.groupInvitations.filter(inv => inv.id !== invitation.id);
        } else {
          // 否则更新状态
          const index = state.groupInvitations.findIndex(inv => inv.id === invitation.id);
          if (index !== -1) {
            state.groupInvitations[index] = invitation;
          }
        }
      });
  },
});

export const { clearError, clearSearchResults } = friendsSlice.actions;
export default friendsSlice.reducer;
